/**
 * ログインページ - Aero Quest デザインシステム
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// ヒーローパネルの背景画像（ドローン編隊飛行）
const HERO_IMAGE_URL =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuAm0Fh6WzAZ_yggLAtL3RrAStF6FHoAm-7Bsn0Hs5Ai5cRIkWtWOEfg6XvElgh9zykmJfli8xS81rVqEyxZHmOy1uz58BOrN3PB16qiR9g0MyQgXExFrkhl6prH_iGC-8IuQKXahq8Ns7akx34lBaUHkSH2agT2G_DncD5dYw4tW-ISQeodKtBOeMnAoAAsj5bIlJ6CyYbEYT3t4FDqAmP5afe2Y8bsZtm99RJANJBl_fml8LhSap-5pwQuM27QH2zQDsv0BqhM5kDG';

// エラーアラート
function ErrorAlert({ message }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-error-container border-2 border-[#ba1a1a]">
      <span className="material-symbols-outlined text-[#93000a] text-xl flex-shrink-0 mt-0.5">error</span>
      <p className="text-sm text-[#93000a]">{message}</p>
    </div>
  );
}

// フォームラベル（Aero Quest label-caps スタイル）
function FieldLabel({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-label-caps"
      style={{
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.1em',
        color: '#414751',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </label>
  );
}

// テキスト入力欄（Material Symbols アイコン付き）
function AeroInput({ id, type = 'text', value, onChange, placeholder, autoFocus, icon, rightButton }) {
  return (
    <div className="relative">
      {icon && (
        <span
          className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 select-none pointer-events-none"
          style={{ color: '#717782', fontSize: '20px' }}
        >
          {icon}
        </span>
      )}
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        required
        className="w-full py-3 bg-surface border-2 border-outline-variant rounded-lg font-body text-body-md placeholder:text-outline transition-colors focus:outline-none focus:border-primary"
        style={{
          paddingLeft: icon ? '2.5rem' : '1rem',
          paddingRight: rightButton ? '3rem' : '1rem',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
        }}
      />
      {rightButton}
    </div>
  );
}

// パスワード入力（表示トグル付き）
function PasswordInput({ id, value, onChange, placeholder }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <span
        className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 select-none pointer-events-none"
        style={{ color: '#717782', fontSize: '20px' }}
      >
        lock
      </span>
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className="w-full py-3 bg-surface border-2 border-outline-variant rounded-lg font-body text-body-md placeholder:text-outline transition-colors focus:outline-none focus:border-primary"
        style={{
          paddingLeft: '2.5rem',
          paddingRight: '3rem',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
        }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors focus:outline-none"
        style={{ color: '#717782' }}
        onMouseEnter={(e) => (e.currentTarget.style.color = '#00508e')}
        onMouseLeave={(e) => (e.currentTarget.style.color = '#717782')}
        tabIndex={-1}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
          {visible ? 'visibility_off' : 'visibility'}
        </span>
      </button>
    </div>
  );
}

// ローディングスピナー
function Spinner() {
  return (
    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

// クエストウィンドウカード（ドラクエ風2重枠）
function QuestWindow({ children }) {
  return (
    <div
      className="relative bg-surface-container-lowest border-4 border-primary rounded-xl p-8"
      style={{ boxShadow: '0 8px 0 0 #00508e' }}
    >
      {/* 内枠（ドラクエ窓枠） */}
      <div
        className="absolute inset-2 border-2 border-surface-variant rounded-lg pointer-events-none"
      />
      {/* コンテンツ */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}

// 初回パスワード変更画面
function NewPasswordForm({ onSubmit, isLoading, errorMessage }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(newPassword, confirmPassword);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ backgroundColor: '#d3e4ff' }}
    >
      {/* テクスチャオーバーレイ */}
      <div
        className="absolute inset-0 z-0 pointer-events-none opacity-40 mix-blend-overlay"
        style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/stardust.png')" }}
      />

      <div className="relative z-10 w-full max-w-md">
        <QuestWindow>
          {/* ヘッダ */}
          <div className="flex flex-col items-center mb-8">
            <div
              className="p-4 rounded-full mb-4"
              style={{
                backgroundColor: '#0068b7',
                boxShadow: '0 4px 0 0 #00508e',
              }}
            >
              <span
                className="material-symbols-outlined text-white"
                style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
              >
                key
              </span>
            </div>
            <h2
              className="text-headline-md text-primary tracking-tight"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              パスワード変更
            </h2>
            <div className="h-0.5 w-16 mt-2 border-b-2 border-dashed border-primary" />
          </div>

          <p className="text-sm text-on-surface-variant text-center mb-6">
            初回ログインのため、新しいパスワードを設定してください
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <ErrorAlert message={errorMessage} />

            <div className="space-y-2">
              <FieldLabel htmlFor="new-password">新しいパスワード</FieldLabel>
              <PasswordInput
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="8文字以上、大小英字・数字含む"
              />
            </div>

            <div className="space-y-2">
              <FieldLabel htmlFor="confirm-password">パスワード確認</FieldLabel>
              <PasswordInput
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="パスワード再入力"
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full text-on-primary font-headline text-lg py-4 rounded-full flex items-center justify-center gap-2 group transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#0068b7',
                  boxShadow: '0 6px 0 0 #00508e',
                  fontFamily: 'Space Grotesk, sans-serif',
                  fontWeight: 600,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.boxShadow = '0 8px 0 0 #00508e';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 6px 0 0 #00508e';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(6px)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.boxShadow = '0 6px 0 0 #00508e';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {isLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <span>パスワードを変更</span>
                    <span
                      className="material-symbols-outlined group-hover:translate-x-1 transition-transform"
                      style={{ fontSize: '20px' }}
                    >
                      check_circle
                    </span>
                  </>
                )}
              </button>
            </div>
          </form>
        </QuestWindow>
      </div>
    </div>
  );
}

// ===== メインコンポーネント =====
export default function Login() {
  const { login, setNewPassword, pendingUser, error } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/');
      }
    } catch (err) {
      setLocalError(err.message || 'ログインに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPassword = async (newPw, confirmPw) => {
    setLocalError('');
    if (newPw !== confirmPw) {
      setLocalError('パスワードが一致しません');
      return;
    }
    if (newPw.length < 8) {
      setLocalError('パスワードは8文字以上で入力してください');
      return;
    }
    setIsLoading(true);
    try {
      const result = await setNewPassword(newPw);
      if (result.success) {
        navigate('/');
      }
    } catch (err) {
      setLocalError(err.message || 'パスワード変更に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 初回パスワード変更画面
  if (pendingUser) {
    return (
      <NewPasswordForm
        onSubmit={handleNewPassword}
        isLoading={isLoading}
        errorMessage={localError || error}
      />
    );
  }

  // 通常ログイン画面
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ backgroundColor: '#d3e4ff' }}
    >
      {/* テクスチャオーバーレイ */}
      <div
        className="absolute inset-0 z-0 pointer-events-none opacity-40 mix-blend-overlay"
        style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/stardust.png')" }}
      />

      {/* 左右2分割コンテナ */}
      <div
        className="relative z-10 w-full max-w-6xl flex rounded-2xl overflow-hidden border-4 border-primary bg-surface-container-lowest"
        style={{
          height: 'min(800px, 90vh)',
          boxShadow: '0 8px 32px rgba(0,80,142,0.2)',
        }}
      >
        {/* ===== 左パネル: ヒーロー画像 ===== */}
        <div
          className="hidden lg:flex w-1/2 relative border-r-4 border-primary"
          style={{
            backgroundImage: `url('${HERO_IMAGE_URL}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* グラデーションオーバーレイ */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, rgba(0,80,142,0.85) 0%, transparent 60%)' }}
          />
          {/* ブランドテキスト */}
          <div className="absolute bottom-12 left-12 right-12 text-white">
            <span
              className="material-symbols-outlined mb-4 block"
              style={{ fontSize: '48px', fontVariationSettings: "'FILL' 1", color: '#fff' }}
            >
              rocket_launch
            </span>
            <h1
              className="text-headline-lg mb-2 tracking-tight"
              style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#fff' }}
            >
              AIRGROW
            </h1>
            <p
              className="text-body-lg max-w-md"
              style={{ color: 'rgba(255,255,255,0.9)', fontFamily: 'Inter, sans-serif' }}
            >
              次世代ドローン管制システム。<br />
              高精度なテレメトリとミッション管理を一つのコンソールで。
            </p>
          </div>
        </div>

        {/* ===== 右パネル: ログインフォーム ===== */}
        <div
          className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-surface-container-lowest relative overflow-y-auto"
          style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/az-subtle.png')" }}
        >
          {/* 背景レーダーアイコン（装飾） */}
          <div className="absolute -top-12 -left-12 pointer-events-none select-none opacity-5">
            <span className="material-symbols-outlined text-primary" style={{ fontSize: '96px' }}>
              radar
            </span>
          </div>

          <div className="w-full max-w-md">
            <QuestWindow>
              {/* カードヘッダ */}
              <div className="flex flex-col items-center mb-8">
                <div
                  className="p-4 rounded-full mb-4"
                  style={{
                    backgroundColor: '#0068b7',
                    boxShadow: '0 4px 0 0 #00508e',
                  }}
                >
                  <span
                    className="material-symbols-outlined text-white"
                    style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
                  >
                    terminal
                  </span>
                </div>
                <h2
                  className="text-headline-md text-primary tracking-tight"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  AIRGROW FlightPlanner
                </h2>
                <div className="h-0.5 w-16 mt-2 border-b-2 border-primary" />
              </div>

              {/* フォーム */}
              <form onSubmit={handleLogin} className="space-y-5">
                <ErrorAlert message={localError || error} />

                {/* メールアドレス */}
                <div className="space-y-2">
                  <FieldLabel htmlFor="email">メールアドレス</FieldLabel>
                  <AeroInput
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    autoFocus
                    icon="person"
                  />
                </div>

                {/* パスワード */}
                <div className="space-y-2">
                  <FieldLabel htmlFor="password">パスワード</FieldLabel>
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="パスワードを入力してください"
                  />
                </div>

                {/* ログインボタン */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full text-on-primary font-headline text-lg py-4 rounded-full flex items-center justify-center gap-2 group transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: '#0068b7',
                      boxShadow: '0 6px 0 0 #00508e',
                      fontFamily: 'Space Grotesk, sans-serif',
                      fontWeight: 600,
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.boxShadow = '0 8px 0 0 #00508e';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 6px 0 0 #00508e';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(6px)';
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.boxShadow = '0 6px 0 0 #00508e';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {isLoading ? (
                      <Spinner />
                    ) : (
                      <>
                        <span>ログイン</span>
                        <span
                          className="material-symbols-outlined group-hover:translate-x-1 transition-transform"
                          style={{ fontSize: '20px' }}
                        >
                          flight_takeoff
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* 新規登録リンク */}
              <div
                className="mt-8 text-center border-t-2 border-dashed border-outline-variant pt-6"
              >
                <p className="text-body-md text-on-surface-variant" style={{ fontFamily: 'Inter, sans-serif' }}>
                  アカウントをお持ちでない方は{' '}
                  <Link
                    to="/register"
                    className="text-primary font-medium hover:underline decoration-2 underline-offset-4"
                  >
                    新規登録
                  </Link>
                </p>
              </div>
            </QuestWindow>
          </div>
        </div>
      </div>
    </div>
  );
}
